import React, { Dispatch, SetStateAction } from 'react';
import { Button } from 'primereact/button';
import { FormField } from '../../FormField/FormField';
import { ExternalUsersSelect } from '../../ExternalUsersSelect/ExternalUsersSelect';
import { UserDisplay } from '../../UserDisplay/UserDisplay';
import { ExternalUserShort } from '../../../types/externalUser';
import { Routine } from '../../../types/Fisiofit/routine';
import { RoutineForm } from '../validators';
import { getInitialRoutineForm, getUserRoutineName } from '../utils/form';
import { RoutineFormFieldProps } from '../types';

type RoutineUserFieldProps = RoutineFormFieldProps & {
  isCreating: boolean;
  isEditing: boolean;
  isNextRoutine: boolean;
  creatingFromUserProfile: boolean;
  creatingNewRoutineForExpired: boolean;
  currentRoutine: Routine | undefined;
  resource: Routine | undefined;
  hasEnded: boolean;
  hasNextRoutine: boolean;
  userHasEditFeature: boolean;
  nextRoutineDate: Date | undefined;
  setCurrentRoutine: Dispatch<SetStateAction<Routine | undefined>>;
  setForm: Dispatch<SetStateAction<RoutineForm>>;
  setCreatingNewRoutineForExpired: Dispatch<SetStateAction<boolean>>;
};

export const RoutineUserField = ({
  form,
  setFormFields,
  validationErrors,
  isCreating,
  isEditing,
  isNextRoutine,
  creatingFromUserProfile,
  creatingNewRoutineForExpired,
  currentRoutine,
  resource,
  hasEnded,
  hasNextRoutine,
  userHasEditFeature,
  nextRoutineDate,
  setCurrentRoutine,
  setForm,
  setCreatingNewRoutineForExpired,
}: RoutineUserFieldProps) => {
  const handleExternalUserChange = (externalUser: ExternalUserShort) => {
    setFormFields({ externalUser, name: getUserRoutineName(externalUser) });
  };

  return (
    <FormField
      labelTitle='Usuario asignado'
      elementId='routine-user'
      error={validationErrors?.externalUser}
      required={isCreating}
    >
      {isCreating && !isNextRoutine && !creatingFromUserProfile && !creatingNewRoutineForExpired ? (
        <ExternalUsersSelect
          id='routine-user'
          placeholder='Elige un usuario sin rutina'
          searchPlaceholder='Busca usuarios sin rutina'
          value={form?.externalUser?.id}
          onChange={handleExternalUserChange}
          isSingle
          error={validationErrors?.externalUser}
          filterAvailableForNewRoutine
        />
      ) : (
        <div className='routine-user-container'>
          <UserDisplay
            name={currentRoutine?.externalUser?.name}
            surnames={currentRoutine?.externalUser?.surnames}
            avatarUrl={currentRoutine?.externalUser?.avatarUrl}
            {...(!isEditing && { navigateTo: `/users/profile/${currentRoutine?.externalUser?.id}` })}
          />
          {!isEditing &&
            !isCreating &&
            !isNextRoutine &&
            !hasEnded &&
            currentRoutine === resource &&
            (!hasNextRoutine ? userHasEditFeature : true) && (
              <Button
                link
                className='next-routine-button'
                label={hasNextRoutine ? 'Ver próxima rutina' : 'Crear próxima rutina'}
                onClick={() => {
                  if (!hasNextRoutine && currentRoutine?.externalUser) {
                    const partialRoutine = {
                      externalUser: currentRoutine?.externalUser,
                      name: getUserRoutineName(currentRoutine?.externalUser),
                      localStartDate: nextRoutineDate?.toISOString(),
                    } as Routine;
                    setCurrentRoutine(partialRoutine);
                    setForm(getInitialRoutineForm(partialRoutine));
                  } else {
                    // currentRoutine is defined here: the button renders only when !isCreating
                    setCurrentRoutine(currentRoutine?.nextRoutine);
                    setForm(getInitialRoutineForm(currentRoutine?.nextRoutine));
                  }
                }}
              />
            )}
          {currentRoutine?.isExpired && userHasEditFeature && (
            <Button
              link
              className='next-routine-button'
              label='Asignar nueva rutina'
              onClick={() => {
                const { organizationId, externalUser, name } = currentRoutine ?? {};
                const partialRoutine = { organizationId, externalUser, name } as Routine;
                setCurrentRoutine(partialRoutine);
                setForm(getInitialRoutineForm(partialRoutine));
                setCreatingNewRoutineForExpired(true);
              }}
            />
          )}
        </div>
      )}
    </FormField>
  );
};
